import { ErrorList } from 'domains/models';
import { get, RestClientResponse } from 'infras/rest_client';
import { JicooApiResponse } from 'presentations/api/common';
import { useCallback, useEffect, useState } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { getSessionItem, setSessionItem } from '../helpers/storage';

interface Options extends SWRConfiguration {
  isUseCacheWhenInitLoading?: boolean;
  cacheExpSeconds?: number;
}

interface RequestResponse extends JicooApiResponse {
  isLoading: boolean;
  revalidate: () => Promise<boolean>;
  isSuccess: boolean;
  status: number;
  errors: ErrorList | null;
  mutate: (data?: any, shouldRevalidate?: boolean | undefined) => Promise<any>;
}

interface InfiniteOptions extends SWRInfiniteConfiguration {
  isUseCacheWhenInitLoading?: boolean;
  cacheExpSeconds?: number;
}

const defaultCacheExpSeconds = 60 * 60 * 24;
const disableRequestCacheWhenInitLoading =
  Number(process.env['DISABLE_REQUEST_CACHE_WHEN_INIT_LOADING']) === 1;

export const useRequest = (
  url: string | null,
  options?: Options,
): RequestResponse | any => {
  const [isInitLoaded, setIsInitLoaded] = useState<boolean>(false);
  const cacheExpSeconds = options?.cacheExpSeconds ?? defaultCacheExpSeconds;
  let isUseCacheWhenInitLoading =
    options && options.isUseCacheWhenInitLoading !== undefined
      ? options.isUseCacheWhenInitLoading
      : true;
  // envで初回キャッシュ無効が指定されていたら強制的にfalseに
  if (disableRequestCacheWhenInitLoading) {
    isUseCacheWhenInitLoading = false;
  }

  const fetcher = useCallback((url: string): Promise<RestClientResponse> => {
    return new Promise(async (resolve, reject) => {
      const result = await get(url);
      setIsInitLoaded(true);
      if (result.isSuccess) {
        return resolve(result);
      } else {
        return reject(result);
      }
    });
  }, []);
  const result = useSWR(url, fetcher, { revalidateOnFocus: true, ...options });

  useEffect(() => {
    if (isUseCacheWhenInitLoading) {
      if (result.data && url) {
        const response = result.data;
        if (response.data) {
          setSessionItem(url, response.data, cacheExpSeconds);
          setIsInitLoaded(true);
        }
      }
    }
  }, [result.data]);

  let response = result.data;

  let isLoading = false;
  if (response === undefined) {
    let cache = null;
    if (isUseCacheWhenInitLoading) {
      if (url && !isInitLoaded) {
        cache = getSessionItem(url);
      }
    }
    isLoading = true;
    response = {
      isSuccess: false,
      data: cache,
      status: 0,
      errors: {
        unknown: result.error,
      },
    };
  }
  return {
    ...response,
    isLoading,
    mutate: result.mutate,
    revalidate: result.mutate,
  };
};

export const useInfiniteRequest = (
  getKey: (index: number, previousPageData: any) => any,
  options?: InfiniteOptions,
) => {
  const [isInitLoaded, setIsInitLoaded] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const cacheExpSeconds = options?.cacheExpSeconds ?? defaultCacheExpSeconds;
  let isUseCacheWhenInitLoading =
    options && options.isUseCacheWhenInitLoading !== null
      ? options.isUseCacheWhenInitLoading
      : true;
  // envで初回キャッシュ無効が指定されていたら強制的にfalseに
  if (disableRequestCacheWhenInitLoading) {
    isUseCacheWhenInitLoading = false;
  }

  const fetcher = useCallback((url: string): Promise<RestClientResponse> => {
    return new Promise(async (resolve, reject) => {
      setIsLoadingMore(true);
      const result = await get(url);
      setIsInitLoaded(true);
      setIsLoadingMore(false);
      if (result.isSuccess) {
        return resolve(result);
      } else {
        return reject(result);
      }
    });
  }, []);

  const { data, size: page, setSize: setPage, error, mutate } = useSWRInfinite<
    RestClientResponse
  >(getKey, fetcher, {
    revalidateOnFocus: true,
    ...options,
  });

  const currentPage = page;
  const url = getKey(currentPage, null);

  useEffect(() => {
    if (isUseCacheWhenInitLoading && currentPage === 1) {
      if (data && data[0] && url) {
        setSessionItem(url, data[0].data, cacheExpSeconds);
        setIsInitLoaded(true);
      }
    }
  }, [data]);

  let response = data;

  let isLoading = false;
  if (response === undefined && currentPage === 1) {
    let cache = null;
    if (isUseCacheWhenInitLoading) {
      if (url && !isInitLoaded) {
        cache = getSessionItem(url);
      }
    }
    isLoading = true;
    response = [
      {
        isSuccess: false,
        data: cache,
        status: 0,
        errors: {
          unknown: error,
        },
      },
    ];
  }

  let result: RestClientResponse = {
    isSuccess: false,
    data: undefined,
    status: 0,
    errors: {},
  };
  if (Array.isArray(response) && response.length > 0) {
    const lastItem = response[response.length - 1];
    result = {
      ...lastItem,
      data: response
        .filter((res) => !!res.data)
        .flatMap((res) => {
          return res.data;
        }),
    };
  }
  return {
    ...result,
    isLoading,
    isLoadingMore,
    isReachingEnd:
      Array.isArray(data) &&
      data.length > 0 &&
      Array.isArray(data[data.length - 1].data) &&
      data[data.length - 1].data.length === 0,
    page,
    mutate,
    setPage,
    revalidate: mutate,
  };
};
